import { useEffect, useState } from "react";

import {
  authenticateAsync,
  AuthenticationType,
  hasHardwareAsync,
  isEnrolledAsync,
  supportedAuthenticationTypesAsync,
} from "expo-local-authentication";

import {
  UnencryptedStorageKey,
  useUnencryptedStorage,
} from "@/hooks/useUnencryptedStorage";
import useAppMonitor from "@/services/AppMonitor";

const useLocalAuthentication = () => {
  // Determines whether the device has a face or fingerprint scanner.
  const [isBiometricAuthAvailable, setIsBiometricAuthAvailable] =
    useState<boolean>(false);

  const [isBiometricAuthEnabled, setIsBiometricAuthEnabled] = useState<
    boolean | undefined
  >();

  const { captureException } = useAppMonitor();

  const [supportedAuthenticationTypes, setSupportedAuthenticationTypes] =
    useState<AuthenticationType[]>([]);

  const { getUnencryptedBooleanValue, saveUnencryptedBooleanValue } =
    useUnencryptedStorage();

  useEffect(() => {
    const getHardwareCompatibility = async () => {
      try {
        const isCompatible = await hasHardwareAsync();
        const isEnrolled = await isEnrolledAsync();

        const isAvailable = isCompatible && isEnrolled;
        setIsBiometricAuthAvailable(isAvailable);
      } catch (caughtErr) {
        captureException(caughtErr);
        setIsBiometricAuthAvailable(false);
      }
    };

    const getSupportedAuthenticationTypes = async () => {
      try {
        const types = await supportedAuthenticationTypesAsync();
        setSupportedAuthenticationTypes(types);
      } catch (caughtErr) {
        captureException(caughtErr);
        setSupportedAuthenticationTypes([]);
      }
    };

    const checkLocalAuthenticationStatus = async () => {
      const isEnabled = await getUnencryptedBooleanValue(
        UnencryptedStorageKey.IsBiometricAuthEnabled,
      );

      setIsBiometricAuthEnabled(isEnabled);
    };

    void checkLocalAuthenticationStatus();
    void getHardwareCompatibility();
    void getSupportedAuthenticationTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setIsLocalAuthenticationEnabled = async (isEnabled: boolean) => {
    setIsBiometricAuthEnabled(isEnabled);
    await saveUnencryptedBooleanValue({
      isTrue: isEnabled,
      key: UnencryptedStorageKey.IsBiometricAuthEnabled,
    });
  };

  const isFingerprintSupported = supportedAuthenticationTypes.includes(
    AuthenticationType.FINGERPRINT,
  );

  const isFaceRecognitionSupported = supportedAuthenticationTypes.includes(
    AuthenticationType.FACIAL_RECOGNITION,
  );

  return {
    authenticateAsync,
    isBiometricAuthAvailable,
    isBiometricAuthEnabled,
    isFaceRecognitionSupported,
    isFingerprintSupported,
    setIsLocalAuthenticationEnabled,
  };
};

type UseLocalAuthentication = ReturnType<typeof useLocalAuthentication>;
export type { UseLocalAuthentication };
export default useLocalAuthentication;
